import React, { useState, useRef, useCallback } from 'react';
import { X, Upload, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/useAuthStore';
import { useTranslation } from '../hooks/useTranslation';

interface AddCarModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCarAdded: () => void;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes

export function AddCarModal({ isOpen, onClose, onCarAdded }: AddCarModalProps) {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    front?: number;
    back?: number;
  }>({});
  const [files, setFiles] = useState<{
    front?: File;
    back?: File;
  }>({});
  const [dragOver, setDragOver] = useState<{
    front: boolean;
    back: boolean;
  }>({
    front: false,
    back: false
  });

  const frontInputRef = useRef<HTMLInputElement>(null);
  const backInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return t.dashboard.vehicles.addNew.errors.fileTooLarge;
    }
    if (!file.type.startsWith('image/')) {
      return t.dashboard.vehicles.addNew.errors.invalidFileType;
    }
    return null;
  };

  const handleFileChange = async (file: File, side: 'front' | 'back') => {
    const error = validateFile(file);
    if (error) {
      setError(error);
      return;
    }

    setFiles(prev => ({ ...prev, [side]: file }));
    setError(null);
  };

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>, side: 'front' | 'back') => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(prev => ({ ...prev, [side]: true }));
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>, side: 'front' | 'back') => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(prev => ({ ...prev, [side]: false }));
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>, side: 'front' | 'back') => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(prev => ({ ...prev, [side]: false }));

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileChange(droppedFile, side);
    }
  }, []);

  const uploadFile = async (file: File, side: 'front' | 'back'): Promise<string> => {
    if (!user) throw new Error('No user found');

    const fileExt = file.name.split('.').pop();
    const timestamp = Date.now();
    const filePath = `${user.id}/${timestamp}-${side}.${fileExt}`;

    const { error: uploadError, data } = await supabase.storage
      .from('vehicle-documents')
      .upload(filePath, file, {
        upsert: true,
        onUploadProgress: (progress) => {
          const percentage = (progress.loaded / progress.total) * 100;
          setUploadProgress(prev => ({ ...prev, [side]: percentage }));
        },
      });

    if (uploadError) throw uploadError;
    
    const { data: { publicUrl } } = supabase.storage
      .from('vehicle-documents')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (!files.front || !files.back) {
      setError(t.dashboard.vehicles.addNew.errors.bothImagesRequired);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Upload both files
      const [frontUrl, backUrl] = await Promise.all([
        uploadFile(files.front, 'front'),
        uploadFile(files.back, 'back')
      ]);

      // Create the car record
      const { error: carError } = await supabase.from('cars').insert({
        profile_id: user.id,
        registration_front_url: frontUrl,
        registration_back_url: backUrl,
        status: 'pending',
        brand: 'Pending Review', // These will be updated by admin
        model: 'Pending Review',
        year: new Date().getFullYear()
      });

      if (carError) throw carError;

      setSuccess(true);
      onCarAdded();
      
      // Close modal after 3 seconds
      setTimeout(() => {
        onClose();
        setFiles({});
        setUploadProgress({});
        setSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error adding car:', err);
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Vehicle Added Successfully
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Your car registration is under review. You will be notified once the review is complete.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t.dashboard.vehicles.addNew.title}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded flex items-start">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t.dashboard.vehicles.addNew.registrationFront}
            </label>
            <div 
              className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 ${
                dragOver.front 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                  : 'border-gray-300 border-dashed'
              } rounded-md relative transition-colors duration-200`}
              onDragOver={(e) => handleDragOver(e, 'front')}
              onDragLeave={(e) => handleDragLeave(e, 'front')}
              onDrop={(e) => handleDrop(e, 'front')}
            >
              <div className="space-y-1 text-center">
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600 dark:text-gray-400">
                  <label
                    htmlFor="front-file"
                    className="relative cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                  >
                    <span>{t.dashboard.vehicles.addNew.uploadFile}</span>
                    <input
                      id="front-file"
                      ref={frontInputRef}
                      name="front-file"
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileChange(file, 'front');
                      }}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t.dashboard.vehicles.addNew.imageRequirements}
                </p>
              </div>
              {files.front && (
                <div className="absolute inset-0 bg-gray-100 dark:bg-gray-700 bg-opacity-90 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                      {files.front.name}
                    </p>
                    {uploadProgress.front !== undefined && (
                      <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4 dark:bg-gray-600">
                        <div 
                          className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress.front}%` }}
                        />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setFiles(prev => ({ ...prev, front: undefined }));
                        if (frontInputRef.current) frontInputRef.current.value = '';
                      }}
                      className="text-sm text-red-600 hover:text-red-500"
                    >
                      {t.common.remove}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t.dashboard.vehicles.addNew.registrationBack}
            </label>
            <div 
              className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 ${
                dragOver.back 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                  : 'border-gray-300 border-dashed'
              } rounded-md relative transition-colors duration-200`}
              onDragOver={(e) => handleDragOver(e, 'back')}
              onDragLeave={(e) => handleDragLeave(e, 'back')}
              onDrop={(e) => handleDrop(e, 'back')}
            >
              <div className="space-y-1 text-center">
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600 dark:text-gray-400">
                  <label
                    htmlFor="back-file"
                    className="relative cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                  >
                    <span>{t.dashboard.vehicles.addNew.uploadFile}</span>
                    <input
                      id="back-file"
                      ref={backInputRef}
                      name="back-file"
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileChange(file, 'back');
                      }}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t.dashboard.vehicles.addNew.imageRequirements}
                </p>
              </div>
              {files.back && (
                <div className="absolute inset-0 bg-gray-100 dark:bg-gray-700 bg-opacity-90 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                      {files.back.name}
                    </p>
                    {uploadProgress.back !== undefined && (
                      <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4 dark:bg-gray-600">
                        <div 
                          className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress.back}%` }}
                        />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setFiles(prev => ({ ...prev, back: undefined }));
                        if (backInputRef.current) backInputRef.current.value = '';
                      }}
                      className="text-sm text-red-600 hover:text-red-500"
                    >
                      {t.common.remove}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
            >
              {t.common.cancel}
            </button>
            <button
              type="submit"
              disabled={loading || !files.front || !files.back}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? t.dashboard.vehicles.addNew.uploading : t.dashboard.vehicles.addVehicle}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}